import { ArrowDownTrayIcon, DocumentIcon, SixDotsIcon, TrashIcon } from '@shared/assets/icons';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { MAX_FILE_SIZE, SUPPORTS_TYPES_FILE } from '@entities/leadCollector';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import ButtonWithPlus from '@shared/ui/button-with-plus';
import { colors } from '@shared/tokens/colors.stylex';
import { Button } from '@shared/ui/kit';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { DeleteConfirmPopup } from '@shared/ui/delete-confirm-popup';

interface FormImageProps {
  value: File | string;
  setValue?: (value: File | null) => void;
  onDelete?: (instanceId: string) => void;
  instanceId?: string;
  isCanEdit?: boolean;
}

export const FormImage = ({ value, setValue, onDelete, instanceId, isCanEdit }: FormImageProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const deleteButtonRef = useRef<HTMLSpanElement>(null);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [src, setSrc] = useState('');
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const hasUrl = e.dataTransfer.types.some(
      (type) => type === 'text/plain' || type === 'text/uri-list' || type === 'text/html'
    );

    if (hasUrl) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer?.files.length) {
      onLoadFile(e.dataTransfer.files[0]);
    }
  }, []);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onLoadFile(e.target.files[0]);
    }
  };

  const onLoadFile = (file: File) => {
    setError('');

    if (file.size > MAX_FILE_SIZE) {
      setError('Размер файла превышает 10 Mb');
      return;
    }

    if (!SUPPORTS_TYPES_FILE?.includes(file.type)) {
      setError('Неподдерживаемый формат файла');
      return;
    }

    setSrc(URL.createObjectURL(file));
    setValue?.(file);
  };

  const handleConfirmDelete = () => {
    if (instanceId) {
      onDelete?.(instanceId);
    }
    setIsDeletePopupOpen(false);
  };

  const handleAddImage = () => {
    setError('');
    inputRef.current?.click();
  };

  useEffect(() => {
    if (value instanceof File) {
      setSrc(URL.createObjectURL(value));
    }

    if (typeof value === 'string') {
      setSrc(value);
    }
  }, []);

  return (
    <div {...stylex.props(styles.formImage)}>
      <div
        {...stylex.props(styles.container, isDragOver && !isCanEdit && styles.dragContainer)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={isCanEdit ? undefined : handleDrop}
      >
        {!src ? (
          <div {...stylex.props(styles.content, isCanEdit && styles.contentPreview)}>
            {!isCanEdit && (
              <>
                <ArrowDownTrayIcon color={colors.inverseIcon} />

                <span {...stylex.props(styles.description)}>Нажмите на кнопку ниже или перетащите файл</span>

                <ButtonWithPlus handleClick={handleAddImage} labelText="Добавить" />
              </>
            )}
          </div>
        ) : (
          <div {...stylex.props(styles.imageContainer, isCanEdit && styles.imageContainerPreview)}>
            <img {...stylex.props(styles.image)} src={src} alt="Обложка" />
          </div>
        )}

        {!isCanEdit && (
          <>
            <div {...stylex.props(styles.moveButton)}>
              <UnstyledButton>
                <SixDotsIcon color={colors.secondaryBackground} />
              </UnstyledButton>
            </div>

            <div {...stylex.props(styles.actions)}>
              <UnstyledButton onClick={() => setIsDeletePopupOpen(true)}>
                <span {...stylex.props(styles.removeBlockButton)} ref={deleteButtonRef}>
                  <TrashIcon color={colors.primaryIcon} />
                </span>
              </UnstyledButton>

              {src && (
                <UnstyledButton onClick={handleAddImage}>
                  <span {...stylex.props(styles.uploadButton)}>
                    <ArrowDownTrayIcon size={16} />
                  </span>
                </UnstyledButton>
              )}

              <DeleteConfirmPopup
                isOpen={isDeletePopupOpen}
                onClose={() => setIsDeletePopupOpen(false)}
                onConfirm={handleConfirmDelete}
                anchorRef={deleteButtonRef}
              />
            </div>
          </>
        )}
      </div>

      <input
        {...stylex.props(styles.inputImage)}
        type="file"
        ref={inputRef}
        value=""
        onChange={onChange}
        accept=".jpg, .jpeg, .png"
      />

      {error && (
        <div {...stylex.props(styles.errorContainer)}>
          <div {...stylex.props(styles.errorContent)}>
            <DocumentIcon color={colors.errorText} />

            <div {...stylex.props(styles.errorText)}>
              <span {...stylex.props(styles.errorTitle)}>Ошибка загрузки</span>
              <span {...stylex.props(styles.errorDescription)}>{error}</span>
            </div>
          </div>

          <Button onClick={() => setError('')}>
            <TrashIcon {...stylex.props(styles.removeButton)} color="inherit" size="20" />
          </Button>
        </div>
      )}
    </div>
  );
};
